'use client'

import Link from 'next/link'
import { ReactNode } from 'react'

import SpaceBackdrop from '@components/ui/space-backdrop'
import Wordmark from '@images/logo/wordmark.svg'
import { APP_NAME, HOMEPAGE_URL } from '@lib/constants'
import { cn } from '@lib/cn'
import { useDocumentTitle } from '@lib/use-document-title'

interface Props {
  children?: ReactNode
  title: string
  heading?: ReactNode
  subheading?: ReactNode
  className?: string
}

const AuthLayout = ({ children, title, heading, subheading, className }: Props) => {
  useDocumentTitle(title)

  return (
    <div className="relative isolate flex min-h-dvh flex-col items-center bg-o-sidebar px-4 font-sans leading-tight">
      <SpaceBackdrop />
      <div className="flex items-center justify-center pb-8 pt-14 md:pb-10 md:pt-[88px]">
        <Link href={HOMEPAGE_URL} aria-label={APP_NAME} className="text-o-ink hover:opacity-80">
          <Wordmark className="h-7 w-auto" />
        </Link>
      </div>
      <main
        className={cn(
          'flex min-h-80 w-full max-w-[420px] flex-col gap-6 rounded-[20px] border border-transparent bg-o-card p-6 shadow-[0_2px_4px_#1D24330A,0_30px_80px_#1D243324] dark:border-o-panel-edge dark:shadow-none md:p-9 md:pb-8',
          className
        )}
      >
        {(heading || subheading) && (
          <div className="flex flex-col gap-2">
            {heading && <h1 className="text-2xl font-medium leading-[30px] tracking-[-0.02em] text-o-ink">{heading}</h1>}
            {subheading && <p className="text-sm leading-5 text-o-muted">{subheading}</p>}
          </div>
        )}
        <div className="flex w-full flex-col gap-3">{children}</div>
      </main>
      <footer className="mt-auto flex gap-3.5 pb-8 pt-12 text-[13px] text-o-slate">
        <a href={HOMEPAGE_URL} className="hover:text-o-ink">
          {APP_NAME}
        </a>
        <a href={`${HOMEPAGE_URL}/docs`} className="hover:text-o-ink">
          Docs
        </a>
      </footer>
    </div>
  )
}

export default AuthLayout
